import React, { useState, useEffect } from "react";
import Dropdown from "../Dropdown/Dropdown";
import Header from "../Header/Header";
import {
  HeroContainer,
  HeroWrapper,
  HeroLeft,
  HeroRight,
  ImageContainer,
  SocialIcons,
  SocialIconLink,
  ScrollDown,
  ScrollLink,
  TechStack,
  TechBadge
} from "./HeroElements";
import { TypeAnimation } from "react-type-animation";

const Hero = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showSubtitle, setShowSubtitle] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [showSocials, setShowSocials] = useState(false);

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSocials(true);
    }, 2500);

    return () => clearTimeout(timer);
  }, []);

  const techStack = [
    // Frontend
    
    { name: "HTML", color: "#E34F26" },
    { name: "CSS", color: "#1572B6" },
    { name: "Tailwind CSS", color: "#38B2AC" },
    { name: "JavaScript", color: "#F7DF1E" },

    { name: "React", color: "#61DAFB" },
    { name: "Redux", color: "#764ABC" },
    { name: "React Router", color: "#F7DF1E" },
    { name: "Framer Motion", color: "#F24E1E" },
    { name: "Three.js", color: "#339933" },
    { name: "Lottie React", color: "#F7DF1E" },
    { name: "Ant Design", color: "#F7DF1E" },
    
    // Backend
    { name: "Node.js", color: "#339933" },
    { name: "Express.js", color: "#339933" },
    { name: "Next.js", color: "#000000" },
    { name: "JWT", color: "#F7DF1E" },
    { name: "Axios", color: "#5A29E3" },
    
    // Database
    { name: "MongoDB", color: "#47A248" },
    { name: "SQL", color: "#E34F26" },
    { name: "Firebase", color: "#FFCA28" },
    
    // Languages
    { name: "C++", color: "#00599C" },
    { name: "C", color: "#00599C" },
    { name: "Java", color: "#007396" },
    
    // Tools & Services
    { name: "Git", color: "#F05032" },
    { name: "GitHub", color: "#339933" },
    { name: "Firebase Auth", color: "#FFCA28" },
    { name: "EmailJS", color: "#F7DF1E" },
    { name: "Cloudinary", color: "#F7DF1E" },
    { name: "Tawk.to", color: "#F7DF1E" },
    { name: "Netlify", color: "#00C7B2" },
    { name: "Render", color: "#F7DF1E" }
  ];

  return (
    <main className="relative">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 opacity-50 z-0"></div>
      <Dropdown isOpen={isOpen} toggle={toggle} />
      <Header toggle={toggle} />
      <HeroContainer>
        <div className="relative z-10">
          <HeroWrapper>
            <HeroLeft>
              <div className="transition-all duration-500 ease-out">
                <TypeAnimation
                  cursor={false}
                  sequence={[
                    "Hi, I'm Dibya",
                    () => setShowSubtitle(true)
                  ]}
                  speed={{ type: "keyStrokeDelayInMs", value: 150 }}
                  wrapper="h1"
                  repeat={0}
                />
                {showSubtitle && (
                  <>
                    <TypeAnimation
                      cursor={true}
                      sequence={[
                        500,
                        "A Frontend Developer.",
                        1000,
                        "A Problem Solver.",
                        1000,
                        "An Innovative Thinker.",
                        1000,
                        "A UI/UX Enthusiast.",
                        1000,
                        "A Coffee Lover ☕",
                        1000,
                        "A Code Craftsman.",
                        1000,
                        "Scroll down to see my work!",
                        300,
                        () => setShowScrollDown(true),
                        1000,
                        "My projects showcase my skills.",
                        1000,
                        "Let's build something amazing together!",
                        1000,
                      ]}
                      speed={50}
                      deletionSpeed={65}
                      wrapper="h5"
                      repeat={Infinity}
                    />
                    
                    <TechStack>
                      {techStack.map((tech, index) => (
                        <div
                          key={index}
                          className="transition-all duration-500 ease-out transform translate-y-0 opacity-100"
                          style={{transitionDelay: `${300 + index * 100}ms`}}
                        >
                          <TechBadge color={tech.color}>
                            {tech.name}
                          </TechBadge>
                        </div>
                      ))}
                    </TechStack>
                  </>
                )}
              </div>

              {showSocials && (
                <SocialIcons>
                  <div className="transform transition-all duration-300 ease-out opacity-100">
                    <SocialIconLink href="https://github.com/DIX2580/" target="_blank" aria-label="Github">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
                      </svg>
                    </SocialIconLink>
                  </div>
                  <div className="transform transition-all duration-300 ease-out opacity-100" style={{transitionDelay: "100ms"}}>
                    <SocialIconLink href="https://www.linkedin.com/in/dibyanjaya/" target="_blank" aria-label="Linkedin">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                        <rect x="2" y="9" width="4" height="12"></rect>
                        <circle cx="4" cy="4" r="2"></circle>
                      </svg>
                    </SocialIconLink>
                  </div>
                  
                  <div className="transform transition-all duration-300 ease-out opacity-100" style={{transitionDelay: "300ms"}}>
                    <SocialIconLink href="mailto:dibyanjayapanda25@gmail.com" aria-label="Email">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                    </SocialIconLink>
                  </div>
                </SocialIcons>
              )}
            </HeroLeft>

            <HeroRight>
              <ImageContainer>
                <img
                  src="/vecteezy_software-engineer-png-graphic-clipart-design_20963004.png"
                  alt="Dibya - Frontend Developer"
                  className="transform transition-all duration-700 ease-out scale-100 opacity-100"
                />
              </ImageContainer>
            </HeroRight>
          </HeroWrapper>
        </div>

        {showScrollDown && (
          <div className="transform transition-all duration-500 ease-out translate-y-0 opacity-100">
            <ScrollDown to="projects" id="scrollDown" smooth={true} duration={500}>
              <ScrollLink>
                Explore My Work
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="scroll-icon ml-2">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </ScrollLink>
            </ScrollDown>
          </div>
        )}
      </HeroContainer>
    </main>
  );
};

export default Hero;